/**
 * Swaps the bird's <img src> through a sequence of frames, with each
 * frame held for a per-frame duration. Mirrors the variable-timing
 * scheduler used by the native app — fixed-interval cycling feels
 * mechanical, variable hold durations look hand-crafted.
 */

import type { KokoAnimationSet, KokoAnimationStateName } from "./animations";
import { KOKO_ANIMATIONS } from "./animations";

export interface BirdRenderer {
  setAnimation(stateName: KokoAnimationStateName, altId?: string): void;
  start(): void;
  stop(): void;
}

export interface PresentableBirdRenderer extends BirdRenderer {
  whenReady(): Promise<void>;
}

/**
 * Structural sprite-renderer type retained for existing controllers. Keeping
 * those consumers coupled only to the three renderer operations lets video
 * and future renderers slot in without changing movement behavior.
 */
export interface SpriteRenderer extends BirdRenderer {}

export const SpriteRenderer: {
  new (imageElement: HTMLImageElement, baseSpriteUrl: string): SpriteRenderer;
} = class SpriteRendererImpl implements BirdRenderer {
  private imageElement: HTMLImageElement;
  private currentAnimation: KokoAnimationSet;
  private currentFrameIndex = 0;
  private pendingTimeout: number | null = null;
  private baseSpriteUrl: string;

  constructor(imageElement: HTMLImageElement, baseSpriteUrl: string) {
    this.imageElement = imageElement;
    this.baseSpriteUrl = baseSpriteUrl.replace(/\/$/, "");
    this.currentAnimation = KOKO_ANIMATIONS.flight;
    // Pixel-art: disable smoothing so upscaled frames stay crisp.
    this.imageElement.style.imageRendering = "pixelated";
    this.applyCurrentFrame();
  }

  /**
   * Switches to a different animation set. If already on that set, this
   * is a no-op so we don't reset the cycle mid-flap.
   */
  setAnimation(stateName: KokoAnimationStateName, _altId?: string): void {
    const nextAnimation = KOKO_ANIMATIONS[stateName];
    if (nextAnimation === this.currentAnimation) {
      return;
    }
    this.currentAnimation = nextAnimation;
    this.currentFrameIndex = 0;
    this.scheduleNextFrame();
    this.applyCurrentFrame();
  }

  /** Begin (or resume) the frame cycle. Safe to call multiple times. */
  start(): void {
    if (this.pendingTimeout !== null) return;
    this.scheduleNextFrame();
  }

  stop(): void {
    if (this.pendingTimeout !== null) {
      window.clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.stop();
  }

  whenReady(): Promise<void> {
    if (this.imageElement.complete && this.imageElement.naturalWidth > 0) {
      return Promise.resolve();
    }
    if (typeof this.imageElement.decode !== "function") {
      return new Promise((resolve) => {
        const onReady = () => resolve();
        this.imageElement.addEventListener("load", onReady, { once: true });
        this.imageElement.addEventListener("error", onReady, { once: true });
      });
    }
    return this.imageElement.decode().catch(() => undefined);
  }

  private applyCurrentFrame(): void {
    const frameName = this.currentAnimation.frameNames[this.currentFrameIndex];
    this.imageElement.src = `${this.baseSpriteUrl}/${frameName}.png`;
  }

  private scheduleNextFrame(): void {
    if (this.pendingTimeout !== null) {
      window.clearTimeout(this.pendingTimeout);
    }
    const holdSeconds =
      this.currentAnimation.frameHoldDurations[this.currentFrameIndex];
    this.pendingTimeout = window.setTimeout(() => {
      this.advanceFrame();
    }, holdSeconds * 1000);
  }

  private advanceFrame(): void {
    const frameCount = this.currentAnimation.frameNames.length;
    const isLastFrame = this.currentFrameIndex >= frameCount - 1;
    if (isLastFrame && !this.currentAnimation.loops) {
      this.pendingTimeout = null;
      return;
    }
    this.currentFrameIndex = (this.currentFrameIndex + 1) % frameCount;
    this.applyCurrentFrame();
    this.scheduleNextFrame();
  }
};
